// Database-aligned user profile types
// These match the actual database schema with additional derived fields

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

// Billing history/invoice interface
export interface BillingHistoryItem {
  id: string;
  date: string | Date;
  amount: number;
  status: string;
  url?: string;
  pdf?: string;
}

// Subscription details interface
export interface SubscriptionDetails {
  id: string;
  stripe_subscription_id: string;
  status: string;
  current_period_start: string | Date;
  current_period_end: string | Date;
  cancel_at_period_end: boolean;
  canceled_at: string | Date | null;
  price_id: string;
  price_interval: string;
  price_amount: number;
  price_currency: string;
}


// Base database user profile schema (matches Postgres table)
export interface DbUserProfile {
    id: string;
    email: string;
    subscription_tier: 'anonymous' | 'free' | 'premium';
    tokens_used_monthly: number;
    tokens_monthly_limit: number;
    created_at?: string;
    updated_at?: string;
    display_name?: string | null;
    preferred_model?: string | null;
    notification_settings?: {
      email_notifications: boolean;
      marketing_emails: boolean;
    };
  }
  
  // API response user profile schema
  export interface ApiUserProfile {
    id: string;
    email: string;
    name?: string | null;
    isAnonymous: boolean;
    isSubscribed: boolean;
    isAdmin?: boolean;
    subscription?: {
      tier: string;
      expiresAt: string;
    };
    usage?: {
      messagesRemaining: number;
      tokenUsage: number;
      resetDate: string;
    };
    preferred_model?: string;
    jurisdiction?: {
      country: string | null;
      state: string | null;
      locality: string | null;
      last_updated: string | null;
    };
  }
  
  // Consolidated UserProfile that our application uses internally
  // This is the type that components will expect to work with
  export interface UserProfile {
    // Core database fields (direct from Postgres)
    id: string;
    email: string;
    subscription_tier: 'anonymous' | 'free' | 'premium';
    tokens_used_monthly: number;
    tokens_monthly_limit: number;
    created_at?: string;
    updated_at?: string;
    display_name?: string | null;
    preferred_model?: string | null;
    notification_settings?: {
      email_notifications: boolean;
      marketing_emails: boolean;
    };
    
    // Derived fields (calculated from database values)
    name?: string | null; // Alias for display_name for API compatibility
    isAnonymous: boolean; // Derived from subscription_tier
    isSubscribed: boolean; // Derived from subscription_tier
    tokens_remaining?: number; // Calculated from limits and usage
    
    // API-specific fields (may be null if data comes from database)
    is_admin?: boolean;
    subscription?: {
      tier: string;
      expiresAt: string;
    };
    usage?: {
      messagesRemaining: number;
      tokenUsage: number;
      resetDate: string;
    };

      // Stripe-related fields
    stripe_customer_id?: string | null;
    stripe_subscription_id?: string | null;
    payment_methods?: PaymentMethod[];
    billing_history?: BillingHistoryItem[];
    subscription_details?: SubscriptionDetails | null;

    jurisdiction?: {
      country: string | null;
      state: string | null;
      locality: string | null;
      last_updated: string | null;
    };
  }
  
  
  
  /**
   * Converts a database user profile to our application's internal format
   */
  export function dbProfileToUserProfile(dbProfile: DbUserProfile): UserProfile {
    return {
      ...dbProfile,
      name: dbProfile.display_name,
      isAnonymous: dbProfile.subscription_tier === 'anonymous',
      isSubscribed: dbProfile.subscription_tier === 'premium',
      tokens_remaining: Math.max(0, dbProfile.tokens_monthly_limit - dbProfile.tokens_used_monthly)
    };
  }
  
  /**
   * Converts an API user profile to our application's internal format
   */
  export function apiProfileToUserProfile(apiProfile: ApiUserProfile): UserProfile {
    const subscriptionTier = 
      apiProfile.isAnonymous ? 'anonymous' : 
      apiProfile.isSubscribed ? 'premium' : 'free';
      
    return {
      id: apiProfile.id,
      email: apiProfile.email,
      display_name: apiProfile.name,
      name: apiProfile.name,
      subscription_tier: subscriptionTier,
      tokens_used_monthly: apiProfile.usage?.tokenUsage || 0,
      tokens_monthly_limit: 
        subscriptionTier === 'premium' ? 200000 : 
        subscriptionTier === 'free' ? 100000 : 20000,
      isAnonymous: apiProfile.isAnonymous,
      isSubscribed: apiProfile.isSubscribed,
      is_admin: apiProfile.isAdmin,
      subscription: apiProfile.subscription,
      usage: apiProfile.usage,
      preferred_model: apiProfile.preferred_model,
      notification_settings: {
        email_notifications: true,
        marketing_emails: false
      },
      // Add jurisdiction data if present
      jurisdiction: apiProfile.jurisdiction
    };
  }
  
  /**
   * Normalizes any profile-like object to our application's internal format
   */
  export function normalizeUserProfile(profile: any): UserProfile {
    if (!profile || typeof profile !== 'object') {
      return createAnonymousProfile();
    }
    
    let normalizedProfile: UserProfile;
    
    // Check if this looks like a database profile
    if ('tokens_monthly_limit' in profile && 'tokens_used_monthly' in profile) {
      normalizedProfile = dbProfileToUserProfile(profile as DbUserProfile);
    }
    // Check if this looks like an API profile
    else if ('isAnonymous' in profile || 'isSubscribed' in profile) {
      normalizedProfile = apiProfileToUserProfile(profile as ApiUserProfile);
    }
    // If we can't determine the type, create a sensible default
    else {
      normalizedProfile = createAnonymousProfile(profile.id, profile.email);
    }
    
    // Ensure jurisdiction data is preserved if present in the source object
    if (profile.jurisdiction) {
      normalizedProfile.jurisdiction = profile.jurisdiction;
    }
    
    return normalizedProfile;
  }
  
  /**
   * Creates a default anonymous user profile
   */
  export function createAnonymousProfile(id?: string, email?: string): UserProfile {
    const profileId = id || crypto.randomUUID();
    
    return {
      id: profileId,
      email: email || '',
      subscription_tier: 'anonymous',
      tokens_used_monthly: 0,
      tokens_monthly_limit: 20000,
      display_name: null,
      name: null,
      isAnonymous: true,
      isSubscribed: false,
      usage: {
        messagesRemaining: 5,
        tokenUsage: 0,
        resetDate: new Date().toISOString()
      },
      tokens_remaining: 20000
    };
  }
  
  /**
   * Helper function to safely access user profile properties,
   * providing a default value if the property or any part of the path is undefined
   */
  export function getUserProfileValue<T>(
    profile: UserProfile | null, 
    path: string, 
    defaultValue: T
  ): T {
    if (!profile) return defaultValue;
    
    const parts = path.split('.');
    let current: any = profile;
    
    for (const part of parts) {
      if (current === null || current === undefined) return defaultValue;
      current = current[part];
    }
    
    return (current !== null && current !== undefined) ? current : defaultValue;
  }
  
  /**
   * Prepares a user profile update for the database
   * Extracts only the fields that exist in the database schema
   */
  export function prepareProfileDbUpdate(updates: Partial<UserProfile>): Partial<DbUserProfile> {
    const dbUpdate: Partial<DbUserProfile> = {};
    
    // Only include fields that exist in the database
    if (updates.display_name !== undefined) dbUpdate.display_name = updates.display_name;
    if (updates.name !== undefined) dbUpdate.display_name = updates.name; // Map name → display_name
    if (updates.preferred_model !== undefined) dbUpdate.preferred_model = updates.preferred_model;
    if (updates.notification_settings !== undefined) dbUpdate.notification_settings = updates.notification_settings;
    if (updates.subscription_tier !== undefined) dbUpdate.subscription_tier = updates.subscription_tier;
    if (updates.tokens_used_monthly !== undefined) dbUpdate.tokens_used_monthly = updates.tokens_used_monthly;
    if (updates.tokens_monthly_limit !== undefined) dbUpdate.tokens_monthly_limit = updates.tokens_monthly_limit;
    
    return dbUpdate;
  }
  
  /**
   * Prepares a user profile update for the API
   * Maps our internal fields to what the API expects
   */
  export function prepareProfileApiUpdate(updates: Partial<UserProfile>): Record<string, any> {
    const apiUpdate: Record<string, any> = {};
    
    // Map fields to what the API expects
    if (updates.display_name !== undefined || updates.name !== undefined) {
      apiUpdate.name = updates.name || updates.display_name;
    }
    
    if (updates.preferred_model !== undefined) {
      apiUpdate.preferred_model = updates.preferred_model;
    }
    
    if (updates.notification_settings !== undefined) {
      apiUpdate.notification_settings = updates.notification_settings;
    }
    
    return apiUpdate;
  }